import React, { useState, useRef, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRange } from 'react-date-range';
import { usePopper } from 'react-popper';
import { Portal } from 'react-portal'
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { animated, useTransition } from 'react-spring';
import { easePoly } from 'd3-ease';
import { addMinutes, format, setHours, setMinutes } from 'date-fns'
import { useGlobalStore } from '@/stores/globalStore';
import useResponsive from '@/hooks/useResponsive';
import { SelectInput } from '@/ui/components/Field/Select';
import { FormLabel } from '@/ui/components/Form/FormLabel';
import translate from './../../../../i18n/translate';
import { dateFnsLocale } from '@/App';
import ArrowIcon from '@/images/ArrowIcon.svg'; 

const times = [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47, 48]

const convertToTimePeriod = (gap) => {
    const totalMinutes = gap * 30
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes - hours * 60

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const formatToGapTime = (date, gap, end = false) => {
    const totalMinutes = gap * 30
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return setMinutes(setHours(date, hours), end ? minutes - 1 : minutes)
}

const DateSelector: React.FC = () => {
    const [open, setOpen] = useState(false)
    const [referenceElement, setReferenceElement] = useState()
    const [popperElement, setPopperElement] = useState()
    const [startTime, setStartTime] = useState(0)
    const [endTime, setEndTime] = useState(48)
    const { isPhone } = useResponsive()

    let { styles, attributes } = usePopper(referenceElement, popperElement)

    const selection = useGlobalStore(state => state.selection)
    const setSelection = useGlobalStore(state => state.setSelection)
    const setSeat = useGlobalStore(state => state.setSeat)
    const setEmployee = useGlobalStore(state => state.setEmployee)

    const ref = useRef<any>()

    const handleSelect = (ranges) => {
        const { startDate, endDate } = ranges.selection
        
        setSelection(formatToGapTime(startDate, startTime), formatToGapTime(endDate, endTime, true))
        setSeat(null)
        setEmployee(null)
    }

    const transitions = useTransition(open, {
        from: { opacity: 0, scale: 0.8 },
        enter: { opacity: 1, scale: 1 },
        leave: { opacity: 0, scale: 0.8 },
        reverse: open,
        config: {
            duration: 300,
            easing: easePoly.exponent(2),
        },
    })

    useEffect(() => {
        let validEndTime = endTime

        if (startTime > endTime) {
            validEndTime = startTime + 1
            setEndTime(validEndTime)

            return
        }

        setSelection(formatToGapTime(selection.startDate, startTime), formatToGapTime(selection.endDate, validEndTime, true))
    }, [startTime, endTime])

    useOnClickOutside(ref, useCallback(() => setOpen(false), []))

    return (
        <ShowContainer ref={setReferenceElement as any}>
            <ShowLabel>
                {translate('appbar_datepicker-title')}
                <img src={ArrowIcon} alt="" style={{ width: 20, height: 20 }} />
            </ShowLabel>
            <SelectedDate onClick={() => setOpen(!open)}>
            <div className="timeBlock">
                <span className="time">{format(selection.startDate, 'HH:mm')}</span>
                <span className="date">{format(selection.startDate, 'dd.MM.yyyy')}</span>
            </div>
            <div className="timeBlock">
                <span className="time">{format(addMinutes(selection.endDate, 1), 'HH:mm')}</span>
                <span className="date">{format(addMinutes(selection.endDate, 1), 'dd.MM.yyyy')}</span>
            </div>
            </SelectedDate>
            {transitions(
                (style, item) => item && (
                    <Portal>
                        <ShowPicker
                            ref={setPopperElement as any}
                            style={{ ...styles.popper, ...style }}
                            {...attributes.popper}
                        >
                            <Wrapper ref={ref}>
                                <DateRange
                                    ranges={[selection]}
                                    onChange={handleSelect}
                                    locale={dateFnsLocale}
                                    months={2}
                                    showDateDisplay={false}
                                    direction={isPhone ? 'vertical' : 'horizontal'}
                                />
                                <TimeWrapper>
                                    <StartDate>
                                    <FormLabel>{translate('start-time')}</FormLabel>
                                        <SelectInput
                                            $fullWidth
                                            value={startTime} 
                                            onChange={(event) => {
                                                const value = event.target.value
                                                setStartTime(Number(value))
                                            }}
                                        >
                                            {times.filter(v => v != 48).map((_, idx) => <option value={idx} key={idx}>{convertToTimePeriod(idx)}</option>)}
                                        </SelectInput>
                                    </StartDate>
                                    <StartEnd>
                                        <FormLabel>{translate('end-time')}</FormLabel>
                                        <SelectInput
                                            $fullWidth
                                            value={endTime}
                                            onChange={(event) => {
                                                const value = event.target.value
                                                setEndTime(Number(value))
                                            }}
                                        >
                                            {times.map((_, idx) => <option key={idx} value={idx}>{convertToTimePeriod(idx)}</option>)}
                                        </SelectInput>
                                    </StartEnd>
                                </TimeWrapper>
                                <AcceptButton onClick={() => setOpen(!open)}>
                                    {translate('select-period')}
                                </AcceptButton>
                            </Wrapper>
                        </ShowPicker>
                    </Portal>
                )
            )}
        </ShowContainer>
    )
}

export default DateSelector

const ShowContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(95, 119, 153, 0.1);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 258px;

  @media (max-width: 600px) {
    gap: 8px;
    min-width: 200px;
  }
`

const ShowLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  color: #2F323C;
`

const SelectedDate = styled.div<{ $open?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 32px;
  transition: border-color 0.2s ease-in-out;

  .timeBlock {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
  }

  .time {
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #2F323C;
  }

  .date {
    font-family: Inter, sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #2F323C;
  }

  @media (max-width: 600px) {
    gap: 16px;

    .time {
      font-size: 12px;
    }

    .date {
      font-size: 16px;
    }
  }
`


const ShowPicker = styled(animated.div)`
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  z-index: 1001;
  max-width: 100%;
  max-height: calc(100% - 150px);
  overflow: auto;
  border-radius: 8px;
  background: #fff;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
`

const TimeWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 32px;
  padding: 20px 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }

  div {
    flex: 1;
  }
`

const StartDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StartEnd = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const AcceptButton = styled.button`
  border: none;
  outline: none;
  background-color: #2e75f2;
  color: #fff;
  padding: 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 6px;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #098f9c;
  }
`
